import { useEffect, useMemo, useState } from "react";
import { Alert, Button, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { PageHeader } from "features/MarketingMaterials/pages/components/PageHeader/PageHeader.tsx";
import useMarketingMaterialsCart from "features/MarketingMaterials/hooks/useMarketingMaterialsCart.ts";
import { useCreateCampaignOrdersMutation } from "features/MarketingMaterials/api/campaignApi.ts";
import {
	useAddMerchandiseShipToMutation,
	useGetMerchandiseAccountsQuery,
	useGetMerchandiseShipTosQuery
} from "features/api/accountApi.ts";
import { models } from "types/api/viewModels.ts";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import AddNewAddressModal from "pages/Replacements/components/AddNewAddressModal/AddNewAddressModal.tsx";
import AddressFormProvider from "pages/Replacements/forms/AddressForm/AddressFormProvider.tsx";
import { AddressFormFields } from "pages/Replacements/forms/AddressForm/schema.ts";
import { formatPhoneNumber } from "components/Replacements/ReplacementsUtil.ts";
import { sortAddresses } from "utils/address.ts";
import * as Styles from "./CampaignShippingPage.styles.ts";
import { CampaignKitCard } from "./components/CampaignKitCard.tsx";
import {
	getCampaignAllocationKey,
	getCampaignSelections,
	getCampaignSummary
} from "features/MarketingMaterials/utils/campaignSelection.ts";
import { formatShippingAddress } from "features/MarketingMaterials/utils/formatShippingAddress.ts";
const errorMessage = (error: any) => error?.errors?.[0]?.message ?? "Unable to place campaign order. Please try again.";
interface CampaignShippingLocationState {
	campaignSelectionKey?: string;
}

export const CampaignShippingPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		cartData,
		campaignItems,
		selectedCampaignKeys,
		selectedMerchAccount,
		setSelectedMerchAccount,
		clearCampaignSelection
	} = useMarketingMaterialsCart();
	const campaignSelectionKey = (location.state as CampaignShippingLocationState | null)?.campaignSelectionKey;
	const effectiveSelectedCampaignKeys = useMemo(
		() => (campaignSelectionKey ? [campaignSelectionKey] : selectedCampaignKeys),
		[campaignSelectionKey, selectedCampaignKeys]
	);
	const selected = useMemo(
		() => campaignItems.filter((item) => effectiveSelectedCampaignKeys.includes(getCampaignAllocationKey(item))),
		[campaignItems, effectiveSelectedCampaignKeys]
	);
	const { data: merchAccountsData, isSuccess: areAccountsLoaded } = useGetMerchandiseAccountsQuery();
	const { data: savedAddressesData } = useGetMerchandiseShipTosQuery(
		{ accountId: selectedMerchAccount?.accountId ?? "" },
		{ skip: !selectedMerchAccount?.accountId }
	);
	const savedAddresses = useMemo(() => savedAddressesData ?? [], [savedAddressesData]);
	const [createCampaignOrders, { isLoading: isSubmitting }] = useCreateCampaignOrdersMutation();
	const [addMerchandiseShipTo] = useAddMerchandiseShipToMutation();
	const [address, setAddress] = useState<models["ShipToViewModel"] | null>(null);
	const [phoneNumber, setPhoneNumber] = useState("");
	const [email, setEmail] = useState("");
	const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!selected.length) navigate("/marketing-materials/pending-campaigns", { replace: true });
	}, [navigate, selected.length]);
	useEffect(() => {
		const merchAccounts = merchAccountsData ?? [];
		if (!areAccountsLoaded || selectedMerchAccount) return;
		const budgetAccounts = merchAccounts.filter((account) => account.hasMerchandiseBudget);
		let defaultAccount: models["CustomerAccountViewModel"] | undefined;
		if (merchAccounts.length === 1) defaultAccount = merchAccounts[0];
		else if (budgetAccounts.length === 1) defaultAccount = budgetAccounts[0];
		if (defaultAccount) setSelectedMerchAccount(defaultAccount);
	}, [areAccountsLoaded, merchAccountsData, selectedMerchAccount, setSelectedMerchAccount]);

	const groupedCampaigns = useMemo(() => getCampaignSummary(selected), [selected]);
	const selectAddress = (option: models["ShipToViewModel"] | null) => {
		setAddress(option);
		setPhoneNumber(formatPhoneNumber(option?.address?.phoneNumber) ?? "");
		setEmail(option?.address?.email ?? "");
	};
	const addAddress = async (form: AddressFormFields) => {
		const formatted = {
			name: form.fullName,
			email: form.email,
			phoneNumber: form.contactPhoneNumber,
			line1: form.address,
			line2: form.address2,
			line3: null,
			city: form.city,
			state: form.state,
			zip: form.zip,
			county: form.county
		};
		try {
			await addMerchandiseShipTo({
				address: formatted,
				accountId: form.merchandiseAddressChoice === "account" ? selectedMerchAccount?.accountId : null
			}).unwrap();
			selectAddress({ shipToId: null, isActive: true, isSelected: true, address: formatted });
			setIsAddressModalOpen(false);
		} catch {
			setError("Unable to save the new address.");
		}
	};
	const submit = async () => {
		setError(null);
		if (!address) return setError("Please select a saved address.");
		const accountId = selectedMerchAccount?.accountId;
		const billToId = selectedMerchAccount?.billTos?.[0]?.billToId;
		const first = selected[0];
		if (!accountId || !billToId || !first) return setError("A merchandise account and Bill To are required.");
		const selections = getCampaignSelections(selected);
		try {
			await createCampaignOrders({
				accountId,
				billToId,
				poNumber: first.campaignName,
				jobName: first.jobName || first.storeNameNumber || "Rep Kit",
				address: {
					...address,
					address: { ...address.address, phoneNumber: phoneNumber || null, email: email || null }
				},
				selections
			}).unwrap();
			clearCampaignSelection();
			navigate("/marketing-materials/pending-campaigns");
		} catch (requestError) {
			setError(errorMessage(requestError));
		}
	};

	return (
		<>
			<PageHeader cartData={cartData} />
			<Styles.CampaignShippingContainer>
				<div>
					<h2>Campaign Order:</h2>
					{groupedCampaigns.map((campaign) => (
						<p key={campaign}>{campaign}</p>
					))}
				</div>
				<h3>Launch Kit</h3>
				{selected.map((item) => (
					<CampaignKitCard
						key={getCampaignAllocationKey(item)}
						item={item}
					/>
				))}
				<h3>Shipping</h3>
				<Styles.ShippingCard>
					<p>SHIP TO</p>
					<p>Required Field (*)</p>
					<div className="address-row">
						<AutoComplete
							options={sortAddresses(savedAddresses)}
							value={address}
							onChange={(_, value) => selectAddress(value)}
							getOptionLabel={formatShippingAddress}
							isOptionEqualToValue={(option, value) =>
								option.shipToId === value.shipToId &&
								formatShippingAddress(option) === formatShippingAddress(value)
							}
							label="Select a Saved Address"
							dataTestId="campaign-shipping-address"
							errorText="Address is required"
							required
							disableClearable={false}
							disabled={!selectedMerchAccount?.accountId}
							noOptionsText="No saved addresses found"
						/>
						<div className="add-address">
							<Button
								variant="text"
								onClick={() => setIsAddressModalOpen(true)}
							>
								+ Add New Address
							</Button>
						</div>
					</div>
					<div>
						<p>Order Contact</p>
						<p>We’ll use this information to contact you about your order.</p>
					</div>
					<div className="contact-row">
						<TextField
							label="Phone (optional)"
							size="small"
							value={phoneNumber}
							onChange={(event) => setPhoneNumber(event.target.value)}
						/>
						<TextField
							label="Email (optional)"
							size="small"
							value={email}
							onChange={(event) => setEmail(event.target.value)}
						/>
					</div>
				</Styles.ShippingCard>
				{error && <Alert severity="error">{error}</Alert>}
				<Styles.PlaceOrderButton
					variant="contained"
					onClick={submit}
					disabled={isSubmitting}
				>
					{isSubmitting ? "PLACING ORDER" : "PLACE ORDER"}
				</Styles.PlaceOrderButton>
			</Styles.CampaignShippingContainer>
			<AddressFormProvider>
				<AddNewAddressModal
					open={isAddressModalOpen}
					handleClose={() => setIsAddressModalOpen(false)}
					onSubmit={addAddress}
					showMerchandiseAddressChoice
				/>
			</AddressFormProvider>
		</>
	);
};
